import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';
import classnames from 'classnames';
import { gtag, install } from 'ga-gtag';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { appWithTranslation, useTranslation } from 'next-i18next';
import { DefaultSeo } from 'next-seo';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import React, { useEffect, useMemo, useState, useCallback } from 'react';

import { IContextProps, MyContext } from '@/config/context-manager';
// 字体从配置文件导入，支持根据环境变量切换 Google Fonts / 本地字体
// 解决国内 Docker 构建时无法访问 Google Fonts 的问题
import { figtree, sourceHanSans } from '@/config/fonts';
import useIsMobile from '@/hooks/useIsMobileNew';
import { Providers } from '@/providers';
import { ReportCookieInfoReq } from '@/request/interface/agreement';
import {
  BASE_URL,
  cdnDomain,
  DISABLE_KAAMEL_PATHS,
  DISABLE_LAYOUT_PATHS,
  DISABLE_LOGIN_PATHS,
  getLoginReportL,
  GTM_ARGUS,
} from '@/utils/constants';
import { mergeQueryParams } from '@/utils/url';
import { hideIntercomUrls } from '@/utils/utils';

import SEO from '../../configs/next-seo.config';

import '@/styles/globals.css';
import '@/styles/themeColor.scss';
import '@/styles/kaamel.custom.scss';
import '@/styles/motion.css';

const GlobalLogin = dynamic(() => import('@/components/GlobalLogin'), {
  ssr: false,
  loading: () => null,
});
const IntercomFacade = dynamic(() => import('@/components/IntercomFacade'), {
  ssr: false,
});
// const sourceHanSans = localFont({
//   src: [
//     {
//       path: '../../public/font/source-han-sans-cn-woff2/SourceHanSansCN-Bold.woff2',
//       weight: '900',
//     },
//   ],
//   variable: '--font-sourceHanSans',
// });

const EXCLUDE_GLOBAL_LOGIN_PATHS = ['/student-center-login'];
// 定义需要独立渲染的页面路径
const STANDALONE_PAGES = ['/app/captcha', '/app/test'];

// 字体已从 @/config/fonts 导入

const geSeoPath = (url: string) => {
  let seoPath = url.replace(/\/[^/]*$/, '/');
  seoPath = seoPath.replace('/zh/', '/');
  seoPath = seoPath.replace('/en/', '/');
  if (seoPath.includes('resources/detail/')) {
    seoPath = seoPath.replace('resources/detail/', 'resources/');
    seoPath = seoPath.replace(/\/$/, 'detail/');
  }
  return seoPath;
};

// canonical 需要指向其它页面的路径映射（key / value 均为去掉语言前缀后的 seoPath，精确匹配）
// /math/ 与 /learn-math/ 竞争同一关键词（Learn Math），按 SEO 需求将 /math/ 的 canonical 指向 /learn-math/：
//   /math/    -> https://www.wukongsch.com/learn-math/
//   /zh/math/ -> https://www.wukongsch.com/zh/learn-math/
const CANONICAL_PATH_MAP: Record<string, string> = {
  '/math/': '/learn-math/',
};

interface NextAppProps extends Omit<AppProps, 'Component'> {
  Component: AppProps['Component'] & {
    getLayout?(page: React.ReactNode): React.ReactElement;
  };
}

// 定义不需要被搜索引擎索引的路由
const NO_INDEX_ROUTES = [
  '/app',
  '/wukong',
  '/zh/app',
  '/zh/wukong',
  // 可以在这里添加更多不需要被索引的路由
];

// 判断当前路由是否需要被搜索引擎索引
const shouldAllowIndexing = (path: string) => {
  // 如果是生产环境且不在 NO_INDEX_ROUTES 中，则允许索引
  if (process.env.NEXT_PUBLIC_STAGE === 'prod') {
    return !NO_INDEX_ROUTES.some(route => path.startsWith(route));
  }
  // 非生产环境不允许索引
  return false;
};

function App({ Component, pageProps }: NextAppProps) {
  const { i18n } = useTranslation(['common']);
  const isMobile = useIsMobile();
  const router = useRouter();
  const [initGTM, setInitGTM] = useState<boolean>(false);
  const [kaamelGAAgree, setKaamelGAAgree] = useState<boolean>(false);
  const [kaamelGTMAgree, setKaamelGTMAgree] = useState<boolean>(false);
  const [frameworkData, setFrameworkData] = useState<{
    framework?: 'GDPR' | 'CCPA' | '';
    finished?: boolean;
  }>();
  const [showGlobalLogin, setShowGlobalLogin] = useState<boolean>(false);
  const [canLoadKaamelScript, setCanLoadKaamelScript] =
    useState<boolean>(false);

  // 定义 handleLinkClick 在组件作用域
  const handleLinkClick = useCallback(
    (event: Event) => {
      const target = (event.target as HTMLElement).closest('a');
      if (target && target.tagName === 'A' && target.href) {
        const newUrl = mergeQueryParams(target.href);
        const isNewWindow = target.target === '_blank';
        if (isNewWindow) {
          window.open(newUrl, '_blank');
          event.preventDefault();
        } else if (newUrl !== target.href) {
          event.preventDefault();
          router.push(newUrl);
        }
      }
    },
    [router],
  );

  // 检查是否是独立页面
  const isStandalonePage = STANDALONE_PAGES.some(
    path => router.pathname === path || router.asPath?.includes(path),
  );

  const { locale } = router;
  const value = useMemo(
    () => ({ locale, framework: frameworkData, kaamelGAAgree, kaamelGTMAgree }),
    [locale, frameworkData, kaamelGAAgree, kaamelGTMAgree],
  );
  const url = router.asPath;
  const isHideIntercom = hideIntercomUrls.some(item =>
    url?.toLowerCase()?.includes?.(item),
  );
  const Layout = Component?.getLayout || (page => page);
  const seoPath = geSeoPath(url);

  // 停用 Google Analytics（分析）
  const disableGA = (disable: boolean) => {
    window[`ga-disable-${process.env.NEXT_PUBLIC_GA_ID}`] = disable;
    window[`ga-disable-${process.env.NEXT_PUBLIC_GA_ID_YOU_FIND}`] = disable;
    window[`ga-disable-${process.env.NEXT_PUBLIC_GA_ID_NEW}`] = disable;
    window[`ga-disable-${process.env.NEXT_PUBLIC_GA_ID_XLP}`] = disable;
  };

  useEffect(() => {
    // 配置 NProgress
    NProgress.configure({
      minimum: 0.4,
      easing: 'ease',
      speed: 400,
      showSpinner: false,
    });

    // 对于独立页面，跳过路由变化处理
    if (isStandalonePage) {
      return () => {};
    }

    const handleRouteChangeStart = () => {
      NProgress.start();
    };

    const handleRouteChangeComplete = () => {
      NProgress.done();
    };

    const handleRouteChangeError = () => {
      NProgress.done();
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [router, isStandalonePage]);

  useEffect(() => {
    const initGatherer = () => {
      import('@wk/wk-gatherer').then(({ init }) => {
        init({
          env: (process.env.NEXT_PUBLIC_STAGE || 'test') as 'test' | 'prod',
          data: { ak: isMobile ? 'newofficial_mobile' : 'newofficial_website' },
        });
      });
    };
    initGatherer();

    // 对于独立页面，跳过路由变化处理
    if (isStandalonePage) {
      return () => {};
    }

    const handleRouteChange = (url: string) => {
      const newUrl = mergeQueryParams(url);
      if (newUrl !== url) {
        router.push(newUrl);
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    const originalWindowOpen = window.open;
    window.open = (url, name, specs) => {
      const newUrl = mergeQueryParams(url as string);
      return originalWindowOpen(newUrl, name, specs);
    };

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
      window.open = originalWindowOpen;
    };
  }, [router, isStandalonePage]);

  // 注册kaamel 服务方法
  useEffect(() => {
    // 对于独立页面，跳过kaamel服务注册
    if (isStandalonePage || !canLoadKaamelScript) {
      return;
    }

    // 打开ga
    const handleGAoPen = () => {
      disableGA(false);
      if (process.env.NEXT_PUBLIC_GA_ID_XLP) {
        install(process.env.NEXT_PUBLIC_GA_ID_XLP, { groups: 'group1' });
        // gtag('config', process.env.NEXT_PUBLIC_GA_ID_YOU_FIND, { groups: 'group1' });
        // gtag('config', process.env.NEXT_PUBLIC_GA_ID_NEW, { groups: 'group1' });
        gtag('config', process.env.NEXT_PUBLIC_GA_ID_XLP, { groups: 'group1' });
        gtag('config', process.env.NEXT_PUBLIC_GA_ID, { groups: 'group1' });
        setKaamelGAAgree(true);
      }
    };
    // 判断是否需要加载kaamel ga也不加载
    const showTip = DISABLE_LAYOUT_PATHS?.some(item => {
      return router.asPath?.startsWith(item);
    });
    if (showTip) {
      return;
    }
    // 不加载kaamel 但是加载ga
    const disableKaamelOnGa = DISABLE_KAAMEL_PATHS?.some(item => {
      return router.asPath?.startsWith(item);
    });
    if (disableKaamelOnGa) {
      handleGAoPen();
      return;
    }
    const script = document.createElement('script');
    script.src = `/kaamel.js?`;
    script.type = 'text/javascript';
    script.id = 'kaameljs';
    document.getElementsByTagName('head')[0].appendChild(script);
    // js 加载后再去判断
    script.onload = () => {
      if (typeof window !== 'undefined' && window.kaamelObj) {
        // 注册kaamel服务
        window.kaamelObj.servicesEffective([
          'analytics',
          'doubleclick',
          'googletagmanager',
        ]);
        // 判断功能是否启用获取具体适配法律框架
        window.kaamelSetting.onFrameworkLoaded = (
          frameworkName: 'GDPR' | 'CCPA' | '',
        ) => {
          setFrameworkData({
            finished: true,
            framework: frameworkName,
          });

          window.kaamel.parameters.horizontalBarFontFamily = '';
          window.kaamel.parameters.mainBarBtnMarginBottom = '0px';
          window.kaamel.parameters.mainBarDenyAllBtnMarginLeft = '0px';
          window.kaamel.parameters.mainBarAcceptAllBtnMarginLeft = '16px';
          window.kaamel.parameters.mainColor = '#cd292a';
          window.kaamel.parameters.horizontalBarFontFamily =
            locale === 'en'
              ? figtree.style.fontFamily
              : 'ui-sans-serif,system-ui,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol,Noto Color Emoji';
        };
      }
    };

    if (typeof window !== 'undefined') {
      window.kaamelSetting = {
        googletagmanager: {
          init() {
            setInitGTM(true);
            setKaamelGTMAgree(true);
          },
          unload() {
            setInitGTM(false);
            setKaamelGTMAgree(false);
          },
        },
        analytics: {
          init() {
            handleGAoPen();
          },
          unload() {
            disableGA(true);
            setKaamelGAAgree(false);
          },
        },
        doubleclick: {
          init() {},
          unload() {},
        },
        dataCollection: (reportData: ReportCookieInfoReq) => {
          if (
            ['new_close_right_banner', 'new_close_right_pannel']?.includes(
              reportData?.triggerId,
            )
          ) {
            return;
          }
          import('@/request/agreement').then(({ reportCookieInfo }) => {
            reportCookieInfo(reportData);
          });
        },
      };
    }
  }, [isStandalonePage, locale, router.asPath, canLoadKaamelScript]);

  useEffect(() => {
    // 对于独立页面，跳过全局登录组件的延迟加载
    if (
      isStandalonePage ||
      EXCLUDE_GLOBAL_LOGIN_PATHS?.includes(router.pathname)
    ) {
      return () => {};
    }
    const handleResourcesLeadId = () => {
      if (!window.sessionStorage.getItem('firstPath')) {
        window.sessionStorage.setItem('firstPath', router.asPath);
      }
    };

    const initEruda = () => {
      // 加载eruda控制台 （ios会缓存提前页面资源， require并不会加载 换一种方式才加载eruda）
      if (
        isMobile &&
        (process.env.NEXT_PUBLIC_STAGE === 'test' ||
          process.env.NEXT_PUBLIC_STAGE === 'dev' ||
          process.env.NEXT_PUBLIC_STAGE === 'develop' ||
          /eruda=true/.test(window?.location?.href))
      ) {
        (function () {
          const script = document.createElement('script');
          script.src = '//cdn.jsdelivr.net/npm/eruda';
          document.body.appendChild(script);
          script.onload = function () {
            window.eruda?.init();
          };
        })();
      }
    };

    // 初始化函数：页面加载完成后执行所有初始化逻辑
    const initializeAfterPageLoad = () => {
      if (!DISABLE_LOGIN_PATHS.some(path => router.asPath.startsWith(path))) {
        setShowGlobalLogin(true);
      }
      initEruda();
      handleResourcesLeadId();
      getLoginReportL();
      setCanLoadKaamelScript(true);
      document.addEventListener('click', handleLinkClick);
    };

    // 检查页面是否已完全加载
    if (document.readyState === 'complete') {
      // 页面已加载完成，添加小延迟确保DCL和LCP已完成
      setTimeout(initializeAfterPageLoad, 100);
    } else {
      // 页面还未加载完成，监听load事件
      window.addEventListener('load', () => {
        setTimeout(initializeAfterPageLoad, 100);
      });
    }

    // 定义一个无返回值的清理函数
    return () => {
      document.removeEventListener('click', handleLinkClick);
      window.removeEventListener('load', initializeAfterPageLoad);
    };
  }, [isStandalonePage, router.pathname, canLoadKaamelScript, handleLinkClick]);

  // 对于独立页面，直接渲染组件，不包含任何全局包装
  if (isStandalonePage) {
    return <Component {...pageProps} />;
  }

  const canonical = () => {
    const lang = i18n.language === 'zh' ? '/zh' : '';
    if (/independent-appointment/.test(url)) {
      return `${BASE_URL}${lang}/independent-appointment/`;
    }
    return `${BASE_URL}${lang}${CANONICAL_PATH_MAP[seoPath] ?? seoPath}`;
  };

  return (
    <MyContext.Provider value={value as IContextProps}>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0, viewport-fit=cover"
        />
        <meta name="applicable-device" content="pc,mobile" />
        <meta httpEquiv="Cache-Control" content="no-transform" />
        <link rel="icon" href={`${cdnDomain}/images/favicon.ico`} sizes="any" />
        <link
          rel="icon"
          type="image/x-icon"
          href={`${cdnDomain}/images/favicon.ico`}
        />
        <link
          rel="shortcut icon"
          href={`${cdnDomain}/images/favicon.ico`}
          // @ts-expect-error 新版本语法
          mce_href={`${cdnDomain}/images/favicon.ico`}
          type="image/x-icon"
        />
        <meta
          data-n-head="ssr"
          data-hid="mobile-web-app-capable"
          name="mobile-web-app-capable"
          content="yes"
        />
        <link
          rel="alternate"
          href={`${BASE_URL}${i18n.language === 'zh' ? '/zh' : ''}${seoPath}`}
          hrefLang={i18n.language}
        />
        <link
          rel="alternate"
          href={`${BASE_URL}${i18n.language === 'zh' ? '' : '/zh'}${seoPath}`}
          hrefLang={i18n.language === 'zh' ? 'en' : 'zh'}
        />
        <link
          rel="alternate"
          href={`${BASE_URL}${seoPath}`}
          hrefLang="x-default"
        />
        <style
          dangerouslySetInnerHTML={{
            __html: `
            :root {
              --kaamel-cookie-title: ${locale === 'en' ? '"Customize Settings"' : '"个性化设置"'}
          }
        `,
          }}
        />
        <script
          id="ksp-seals-script"
          data-product="m5SdFDbXh8cGL4LdnR230C3KnGRyWQXsAVcw4Hfs"
          src="https://kidsafe.com/js/seals/seals.js"
          async
        />
      </Head>
      <DefaultSeo
        {...SEO}
        norobots
        additionalMetaTags={[
          {
            name: 'robots',
            content: shouldAllowIndexing(router.asPath)
              ? 'index, follow'
              : 'noindex, nofollow',
          },
        ]}
        canonical={canonical()}
      />
      {!isHideIntercom && (
        <IntercomFacade
          appId="tn0n4uq4"
          autoOpen={!!router.query.openIntercome}
        />
      )}
      <div
        className={classnames(
          figtree.variable,
          sourceHanSans.variable,
          locale === 'en' && 'font-figtree',
        )}>
        <GTMProvider state={{ ...GTM_ARGUS, injectScript: initGTM }}>
          <Providers>
            <>
              <div>{Layout(<Component {...pageProps} />)}</div>
              {!EXCLUDE_GLOBAL_LOGIN_PATHS?.includes(router.pathname) &&
                showGlobalLogin && <GlobalLogin />}
              {/* {!isHideIntercom && <FloatPhone />} */}
            </>
          </Providers>
        </GTMProvider>
      </div>
    </MyContext.Provider>
  );
}

export default appWithTranslation(App);
